import React from 'react';
import { Play, Check, CheckCircle, Download, Trash2 } from 'lucide-react';
import type { ComponentResult } from '@core/types/slot-screen-component.types';

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  if (diff < 60_000) return 'now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

interface GeneratorGalleryCardProps {
  result: ComponentResult;
  isSelected: boolean;
  onClick: () => void;
  onSelect: () => void;
  onDelete: () => void;
}

export function GeneratorGalleryCard({
  result,
  isSelected,
  onClick,
  onSelect,
  onDelete,
}: GeneratorGalleryCardProps) {
  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = result.type === 'video' && result.videoUrl ? result.videoUrl : result.imageUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `component-${result.id}.${result.type === 'video' ? 'mp4' : 'png'}`;
    a.click();
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      className={`break-inside-avoid mb-3 rounded-lg overflow-hidden border ${
        isSelected ? 'border-green-500 ring-2 ring-green-500/30' : 'border-zinc-800'
      } bg-zinc-900 group cursor-pointer`}
    >
      {/* Image/Video */}
      <div className="relative" onClick={onClick}>
        {result.type === 'video' ? (
          <div className="relative">
            <img src={result.imageUrl} alt="" className="w-full" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                <Play size={20} className="text-white ml-0.5" />
              </div>
            </div>
          </div>
        ) : (
          <img src={result.imageUrl} alt="" className="w-full" />
        )}

        {/* Hover overlay with action buttons */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100">
          <div className="flex gap-1">
            <button
              title="Use"
              onClick={handleSelect}
              className="p-1.5 bg-zinc-800/80 rounded hover:bg-green-600 transition-colors"
            >
              {isSelected ? (
                <CheckCircle size={14} className="text-white" />
              ) : (
                <Check size={14} className="text-white" />
              )}
            </button>
            <button
              title="Save"
              onClick={handleSave}
              className="p-1.5 bg-zinc-800/80 rounded hover:bg-blue-600 transition-colors"
            >
              <Download size={14} className="text-white" />
            </button>
            <button
              title="Delete"
              onClick={handleDelete}
              className="p-1.5 bg-zinc-800/80 rounded hover:bg-red-600 transition-colors"
            >
              <Trash2 size={14} className="text-white" />
            </button>
          </div>
        </div>

        {/* Selected badge */}
        {isSelected && (
          <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
        )}
      </div>

      {/* Metadata footer */}
      <div className="px-2 py-1.5 text-[10px] text-zinc-500 flex items-center justify-between">
        <span>{result.model}</span>
        <span>{formatRelativeTime(result.timestamp)}</span>
      </div>
    </div>
  );
}
